import type { ApiResponse } from '@shared/api'

import { getAxiosErrorMessage } from '@shared/api'
import { useAdminRoutes } from '@shared/lib/routes'
import { useToast } from '@shared/ui/toast'

const SALES_TAX_FILE_FIELD = 'sales_tax_file'

interface SalesTaxRatesImportApiOptions {
  file: File
  /** 0–100. Only fires while the browser reports a computable total. */
  onProgress?: (percent: number) => void
  onStart?: () => void
  onSuccess?: () => void
  onError?: (message: string) => void
  onFinish?: () => void
}

/**
 * `POST /admin-api/sales-tax-rates/import` — multipart CSV/TXT upload that fully replaces the
 * sales-tax rate table. Mirrors the media-upload hook: no global loader (the caller owns progress
 * via the callbacks) and no `Content-Type` header, so axios sets the multipart boundary itself.
 */
export const useSalesTaxRatesImportApi = async (
  options: SalesTaxRatesImportApiOptions,
): Promise<boolean> => {
  const { $axios } = useNuxtApp()
  const toast = useToast()

  const url = useAdminRoutes('admin.sales-tax-rates.import')

  options.onStart?.()

  try {
    if (!url) {
      const message = 'Unexpected error. Invalid request URL.'
      toast.error(message)
      options.onError?.(message)
      return false
    }

    const formData = new FormData()
    formData.append(SALES_TAX_FILE_FIELD, options.file)

    const { data } = await $axios.post<ApiResponse>(url, formData, {
      onUploadProgress: (event) => {
        if (!event.total) return
        options.onProgress?.(Math.round((event.loaded / event.total) * 100))
      },
    })

    if (!data.success) {
      toast.error(data.message)
      options.onError?.(data.message)
      return false
    }

    toast.success(data.message)
    options.onSuccess?.()
    return true
  } catch (err: unknown) {
    const message = getAxiosErrorMessage(err)
    toast.error(message)
    options.onError?.(message)
    return false
  } finally {
    options.onFinish?.()
  }
}
